import qs from "qs";
import queryString from "query-string";
import { differenceInCalendarDays, parseISO } from "date-fns";
import apiClient from "../api/client";

import { payment } from "../constants/actionTypes";
import { paymentTypes } from "../constants/paymentTypes";
import { actions as listingActions } from "./listing";
import { actions as mainActions } from "./main";
import { modals as modalsConstant } from "../constants/listings";
import { normalizeSubscriptions } from "../helpers";
import { resolveFinancialTeamOwnerId } from "../helpers/listingTeamContext";
import { resolveApiErrorMessage } from "../helpers/resolveApiErrorMessage";
import { isCanceledRequest } from "../helpers/isCanceledRequest";
import { getSafeErrorText } from "../helpers/safeRender";
import { track, trackDataLayer } from "../helpers/track";
import { eventName, eventNameDataLayer } from "../constants/track";

const CARDS_LOAD_ERROR_MESSAGE = "We couldn't load your payment methods. Please refresh the page and try again.";
const PDF_ERROR_MESSAGE = "We couldn't open this invoice. Please try again, or contact support if it keeps failing.";
const PDF_FALLBACK_FILE_NAME = "invoice.pdf";
const PDF_OBJECT_URL_LIFETIME_MS = 60_000;

// get-invoice answers a failure as JSON, but the request asks for a blob, so axios hands the error body over
// as a Blob. FileReader rather than Blob.text() so this also runs in Safari before 14 and in jsdom.
const readBlobAsText = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

// Only a 402 carries text meant for the member ("Wrong invoice# passed", "Invoice not found"). A 403 body embeds
// the invoice and user ids (json-api PaymentController getInvoiceAction), so every other failure, and a body
// that cannot be read, gets the fixed message.
const resolvePdfErrorMessage = async error => {
  const response = error?.response;

  if (response?.status !== 402) {
    return PDF_ERROR_MESSAGE;
  }

  try {
    const body = response.data instanceof Blob ? JSON.parse(await readBlobAsText(response.data)) : response.data;
    const message = body?.error;

    return typeof message === "string" && message.trim() ? message : PDF_ERROR_MESSAGE;
  } catch {
    return PDF_ERROR_MESSAGE;
  }
};

// The backend names the file (PaymentController sendData(..., getFileName())). The header is only readable
// from the dashboard origin when the API exposes it with Access-Control-Expose-Headers.
const resolvePdfFileName = headers => {
  const disposition = typeof headers?.get === "function" ? headers.get("content-disposition") : headers?.["content-disposition"];
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || "");
  const raw = match ? match[1].trim() : "";

  try {
    return decodeURIComponent(raw) || PDF_FALLBACK_FILE_NAME;
  } catch {
    return raw || PDF_FALLBACK_FILE_NAME;
  }
};

// An anchor click is a download, not a popup: no blocker, no window handle, and the browser owns the blob once
// click() returns. The URL is still revoked on a timer, because some engines start reading it after the click.
const savePdf = (blob, fileName) => {
  const fileUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
  const link = document.createElement("a");

  link.href = fileUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(fileUrl), PDF_OBJECT_URL_LIFETIME_MS);
};

// Only one payment-history request may be in flight: the endpoint loads the member's whole history before slicing,
// and a slower older answer would otherwise replace a newer page (or, after an account switch, land in the new
// account's slice). Same shape as inboxListController in actions/messages.js.
let paymentHistoryAbortController = null;

// Same for the card list (GROWTH-7383): a refetch after a card action, or a payment modal's fetch on open, replaces the
// one in flight, so an older list or default card never lands over a newer one.
let paymentMethodsAbortController = null;

// The links whose invoice request is in flight, kept in their own state (preloaders.invoicePdf) and not in the history
// list's flag, so a download is never read as "the list is reloading". A list, so two rows can download at once.
const pendingInvoiceLinks = state => state.payment.preloaders.invoicePdf;

export const actions = {
  /**
   * The client secret of a new SetupIntent: what Stripe.js needs to run a card through the issuer's challenge (3-D Secure)
   * before it is saved for later charges (GROWTH-6656). The server creates the member's Stripe customer first when there is
   * none, and answers 400 while the Stripe plugin's "Use PaymentMethods" setting is off.
   *
   * @returns {Promise<string>} the secret; rejects when the request fails or the answer carries none.
   */
  createSetupIntent() {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      return apiClient({
        method: "post",
        url: `/members/json-api${teamUrl}/payment/setup-intent`,
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        data: qs.stringify(userIdParams)
      }).then(res => {
        const clientSecret = res?.data?.client_secret;

        if (!clientSecret) {
          throw new Error("setup-intent answered without a client_secret");
        }

        return clientSecret;
      });
    };
  },

  addCard(cardData) {
    return (dispatch, getState) => {
      const {
        account: { profile },
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      dispatch(this.setLoading("addCard", true));
      dispatch(mainActions.togglePaymentError(false, ""));
      // A rejection from an earlier attempt must not sit in the form beside this request.
      dispatch({
        type: payment.ADD_CARD_FAIL,
        payload: null
      });
      apiClient({
        method: "post",
        url: `/members/json-api${teamUrl}/payment/card-add`,
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        data: qs.stringify({ ...cardData, ...userIdParams })
      })
        .then(() => {
          dispatch({
            type: payment.ADD_CARD_SUCCESS,
            payload: true
          });

          track(eventName.addPaymentInfo, { em: profile.email, fn: profile.name_f, ln: profile.name_l });

          dispatch(this.getPaymentMethods());
        })
        .catch(error => {
          dispatch({
            type: payment.ADD_CARD_FAIL,
            payload: error?.response?.data.error
          });
          dispatch(mainActions.togglePaymentError(true, error?.response?.data?.error));
        })
        .finally(() => dispatch(this.setLoading("addCard", false)));
    };
  },

  removeAddCardSuccess() {
    return dispatch => {
      dispatch({
        type: payment.ADD_CARD_SUCCESS,
        payload: false
      });
    };
  },

  /**
   * Charges (or renews) a listing's membership, then reloads, publishes and tracks the purchase.
   *
   * @param {Object} [options]
   * @param {boolean} [options.armListingsModals=true] GROWTH-6618: only the Listings and Listing Detail pages render the Listings
   *   payment-success modal. A caller on another page (pro bono has its own success modal) passes false, so the modal is not left
   *   armed in the listing slice for the member's next visit to /listings.
   * @param {string|number|null} [options.memberDataReloadId=null] GROWTH-7201: Listing Detail passes the id of the listing it shows.
   *   Paying a Pending Payment listing publishes nothing, and nothing else on that page fetches the listing again, so it
   *   would keep saying Pending Payment. The Listings table reloads its rows with isReload instead.
   */
  chargeForMembership(paymentData, paymentType, isReload, pathname, navigate, profession, options = {}) {
    return (dispatch, getState) => {
      const {
        account: { profile }
      } = getState();
      const { armListingsModals = true, memberDataReloadId = null } = options;
      // GROWTH-7414: the plan being bought, as upgrade-options named it. The reducer shows this instead of guessing a name from
      // the old one. Only an upgrade changes the product (renewals and pending payments keep it), and only when the options
      // on screen belong to the listing being charged.
      const upgradeOptions = getState().listing?.activeUpgradeOptions;
      const upgradedProductName =
        paymentType !== paymentTypes.RENEW &&
        paymentType !== paymentTypes.PENDING &&
        upgradeOptions?.product &&
        Number(upgradeOptions.listing_id) === Number(paymentData.listing_id)
          ? upgradeOptions.product
          : undefined;
      // GROWTH-7126: a team Admin buys for the owner's listing on the owner's card, so the request goes to the -team
      // API with the owner's user_id, as the card and publish requests already do.
      const teamOwnerId = resolveFinancialTeamOwnerId(getState);
      dispatch(this.setLoading("chargeForMembership", true));
      dispatch(mainActions.togglePaymentError(false, ""));
      let isActivePublish = true;
      const { added, trackingData, ...restPaymentData } = paymentData;
      const pattern = /jobs|events|organizations|profile/;
      const isProfile = pathname.includes("/profile");
      const updateListingName = profession || pathname.match(pattern);
      const listingName = isProfile ? "profiles" : updateListingName;
      let command = "upgrade";
      // GROWTH-7201: a Pending Payment listing already has its invoice. json-api listing/charge pays it by listing id
      // with the chosen card; there is no api-v2 twin, no command, and nothing is published afterwards (the listing is
      // not a draft, and the paid invoice moves it out of Pending Payment on the server).
      const isPendingPayment = paymentType === paymentTypes.PENDING;

      let urlUpgrade;
      switch (paymentType) {
        case paymentTypes.RENEW:
          urlUpgrade = `/members/json-api${teamOwnerId ? "/team" : ""}/listing/renew`;
          isActivePublish = false;
          command = "renew";
          break;
        case paymentTypes.PENDING:
          urlUpgrade = `/members/json-api${teamOwnerId ? "/team" : ""}/listing/charge`;
          isActivePublish = false;
          break;
        default:
          urlUpgrade = `/members/json-api${teamOwnerId ? "/team" : ""}/listing/upgrade`;
      }

      const useApiV2 = Boolean(listingName) && !isPendingPayment;
      const url = useApiV2 ? `/members/api-v2${teamOwnerId ? "-team" : ""}/${listingName}` : urlUpgrade;

      apiClient({
        method: useApiV2 ? "put" : "post",
        url,
        data: { ...restPaymentData, ...(isPendingPayment ? {} : { command }), ...(teamOwnerId ? { user_id: teamOwnerId } : {}) }
      })
        .then(res => {
          const searchParams = queryString.parse(window.location.search);
          // api-v2 wraps the payload in a {success, data, ...} envelope; json-api returns it flat.
          const responseData = res.data?.data || res.data || {};

          dispatch({
            type: payment.CHARGE_FOR_MEMBERSHIP_SUCCESS,
            payload: paymentData.listing_id,
            ...(upgradedProductName ? { meta: { productName: upgradedProductName } } : {})
          });

          if (armListingsModals) {
            dispatch(listingActions.closeAllModals());
            dispatch(listingActions.toggleActivityOfModal(modalsConstant.paymentSuccess, true));
          }

          if (isReload) {
            dispatch(listingActions.getExistingMembershipsNew({}, true, searchParams.page, [], [], [], "", "", 10, [], [], "", pathname));
          }

          if (isActivePublish) {
            dispatch(listingActions.publishListing(paymentData.listing_id, isReload, pathname, navigate));
          }

          if (isPendingPayment && memberDataReloadId) {
            dispatch(
              listingActions.getMemberDataNew(memberDataReloadId, pathname, navigate, false, { forceFullExpand: true, skipLoading: true })
            );
          }

          // GROWTH-8128: every upgrade is reported, whatever the listing's age; the 30-day gate hid 4 of 9 upgrades in a year.
          // listing_age_days lets GA still pick out new-listing upgrades. `added` is "YYYY-MM-DD HH:MM:SS"; left out if unreadable.
          if ([paymentTypes.UPGRADE, paymentTypes.PENDING].includes(paymentType)) {
            // Charged amount and invoice id are authoritative from the response; omitted (not guessed) if the backend can't supply them.
            const chargedAmount = responseData.renewal_amount != null ? Number(responseData.renewal_amount) : undefined;
            const invoicePublicId = responseData.invoice_public_id || undefined;
            const appliedCoupon = restPaymentData.coupon || undefined;
            const listingAgeDays = typeof added === "string" ? differenceInCalendarDays(new Date(), parseISO(added)) : NaN;

            track(eventName.purchase, {
              ...(chargedAmount != null ? { value: chargedAmount.toFixed(2) } : {}),
              currency: "USD",
              em: profile.email,
              fn: profile.name_f,
              ln: profile.name_l
            });
            trackDataLayer(eventNameDataLayer.listingUpgrade, {
              listing_status: "in review",
              ...(Number.isFinite(listingAgeDays) ? { listing_age_days: Math.max(0, listingAgeDays) } : {}),
              // GROWTH-7313: both come from the listing row (Apiv2\Query\Listings); left out rather than pushed as null.
              ...(trackingData._heritage ? { heritage: trackingData._heritage } : {}),
              profession: trackingData._profession,
              ...(trackingData._directory ? { directory: trackingData._directory } : {}),
              specialty: trackingData._specialty,
              country: trackingData._country,
              state: trackingData._state,
              city: trackingData._city,
              ecommerce: {
                currency: "USD",
                ...(chargedAmount != null ? { value: chargedAmount } : {}),
                ...(invoicePublicId ? { transaction_id: invoicePublicId } : {}),
                ...(appliedCoupon ? { coupon: appliedCoupon } : {}),
                items: [
                  {
                    // GROWTH-7313: the plan being bought, which the Confirm modals pass as trackingData.product, never the listing's
                    // current "Free ..." product. GA4 reads price and quantity as numbers; price is the amount charged, as `value` is.
                    ...(trackingData.product ? { item_name: trackingData.product } : {}),
                    item_id: trackingData.listing_id,
                    ...(chargedAmount != null ? { price: chargedAmount } : {}),
                    item_category: "listing_upgrade",
                    quantity: 1
                  }
                ]
              }
            });
          }
        })
        .catch(error => {
          dispatch({
            type: payment.CHARGE_FOR_MEMBERSHIP_FAIL,
            payload: error
          });
          // api-v2 answers { errors }, json-api { error } (GROWTH-7062); "" lets PaymentError show its own fallback.
          dispatch(mainActions.togglePaymentError(true, resolveApiErrorMessage(error, "")));
        });
    };
  },

  clearState() {
    return dispatch => {
      dispatch({
        type: payment.CLEAR_STATE
      });
    };
  },

  clearSuccess() {
    return dispatch => {
      dispatch({
        type: payment.CLEAR_SUCCESS
      });
    };
  },

  deleteCard(cardToDelete) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      dispatch(this.setLoading("deleteCard", true));
      dispatch(mainActions.togglePaymentError(false, ""));
      apiClient({
        method: "post",
        url: `/members/json-api${teamUrl}/payment/card-delete`,
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        data: qs.stringify({ ...cardToDelete, ...userIdParams })
      })
        .then(res => {
          dispatch({
            type: payment.DELETE_CARD_SUCCESS,
            payload: true
          });

          dispatch({
            type: payment.DELETE_CARD_LOCALLY,
            payload: res.data
          });

          dispatch(this.selectCard(null));

          setTimeout(
            () =>
              dispatch({
                type: payment.DELETE_CARD_SUCCESS,
                payload: null
              }),
            1
          );

          // Stripe picks the new default when the default card goes, and the subscription rows carry their card's brand and last
          // four from the last cards-all, so ask again instead of guessing. Returned: the .finally below clears the overlay only
          // after the fresh list has landed (GROWTH-6634).
          return dispatch(this.getPaymentMethods());
        })
        .catch(error => {
          // A 403 from card-delete means this card pays for an active subscription
          // (pinned, or the default source for an unpinned invoice). Show the
          // translated explanation rather than the raw server string.
          const message = error?.response?.status === 403 ? "dashboard_payment_methods_remove_active_card" : error?.response?.data?.error;

          dispatch(mainActions.togglePaymentError(true, message));
        })
        .finally(() => dispatch(this.setLoading("deleteCard", false)));
    };
  },

  getPaymentMethods(page, perPage, preferredDefaultCardId = null) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const requestedOwner = currentTeam?.value;

      paymentMethodsAbortController?.abort();
      const controller = new AbortController();
      paymentMethodsAbortController = controller;

      // A reply that must not be written: its request was replaced, or the account was switched while it was on its way.
      const isStale = () => controller.signal.aborted || getState().teams?.currentTeam?.value !== requestedOwner;

      dispatch(this.setLoading("paymentMethods", true));
      const pageParam = page || String(page) === "0" ? { page } : {};
      const perPageParam = perPage ? { per_page: perPage } : {};
      const userId = currentTeam.value ? { user_id: currentTeam.value } : {};
      const filterParams = queryString.stringify({ ...perPageParam, ...userId, ...pageParam }, { arrayFormat: "index" });

      return apiClient({
        method: "get",
        responseType: "json",
        url: `/members/json-api${teamUrl}/payment/cards-all?${filterParams}`,
        signal: controller.signal
      })
        .then(res => {
          if (isStale()) {
            return;
          }

          const {
            cards,
            subscriptions,
            expired_subscriptions: expiredSubscriptions,
            cancelled_subscriptions: cancelledSubscriptions,
            has_stripe_customer: hasStripeCustomer = true,
            cards_unavailable: cardsUnavailableFlag
          } = res.data;
          // GROWTH-6647: when Stripe cannot be reached the server still answers 200, with the subscriptions from the database,
          // `cards: []` and cards_unavailable (GROWTH-6624). That empty list is not "no cards".
          const cardsUnavailable = cardsUnavailableFlag === true;

          dispatch({
            type: payment.GET_PAYMENT_METHODS,
            payload: {
              cards,
              subscriptions: normalizeSubscriptions(subscriptions, cards, hasStripeCustomer),
              expiredSubscriptions: normalizeSubscriptions(expiredSubscriptions, cards, hasStripeCustomer),
              // Cancelled but still open (GROWTH-6649). Present only when the server listed some, so the payload of a
              // response without them is the one it always was; the reducer reads an absent key as none.
              ...(cancelledSubscriptions?.length
                ? { cancelledSubscriptions: normalizeSubscriptions(cancelledSubscriptions, cards, hasStripeCustomer) }
                : {}),
              // Present only when set, for the same reason; the reducer reads an absent key as false.
              ...(cardsUnavailable ? { cardsUnavailable: true } : {})
            }
          });

          if (cardsUnavailable) {
            // The member is told as for a failed request: the card list did not load.
            dispatch(mainActions.togglePaymentError(true, CARDS_LOAD_ERROR_MESSAGE));
          }

          const isDefaultCard = card => card.default_source === true || card.default_source === 1 || card.default_source === "true";

          let defaultSource = cards?.find(isDefaultCard);

          if (preferredDefaultCardId) {
            const preferredCard = cards?.find(({ id }) => id === preferredDefaultCardId);

            if (preferredCard) {
              defaultSource = { ...preferredCard, default_source: true };
            }
          }

          dispatch({
            type: payment.SET_DEFAULT_SOURCE,
            payload: defaultSource
          });
        })
        .catch(error => {
          // A cancel (its replacement answers for the flag) or a failure for an account no longer current dispatches
          // nothing: not the FAIL, no toast. A cancel resolves, so deleteCard's chain on this promise still settles.
          if (isCanceledRequest(error) || isStale()) {
            return;
          }

          dispatch({
            type: payment.GET_PAYMENT_METHODS_FAIL,
            payload: error?.response?.data
          });
          // A fixed message: a cards-all 403 body is { ok: false, error: "Forbidden" }, and a raw reason phrase must not reach a member.
          dispatch(mainActions.togglePaymentError(true, CARDS_LOAD_ERROR_MESSAGE));
        })
        .finally(() => {
          // Only the request that is still current clears the flag: a cancelled one would otherwise turn it off over
          // its replacement's loading list.
          if (paymentMethodsAbortController === controller) {
            paymentMethodsAbortController = null;
            dispatch(this.setLoading("paymentMethods", false));
          }
        });
    };
  },

  /**
   * Points one subscription's invoice at a saved card.
   *
   * @param {{ card_id: string, invoice_id: number }} data
   * @returns {Function} thunk; its promise resolves once the card is saved and the card list has been refetched, and rejects
   *   with the request's own error after the failure toast has been raised
   */
  assignCardToSubscription(data) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      dispatch(this.setLoading("updatePaymentMethod", true));
      dispatch(mainActions.togglePaymentError(false, ""));

      // Returned, and rejecting on failure, so the Update Payment Method modal can tell a saved card from a refused one. The
      // refetch is returned too: the modal then closes once the row behind it already shows the new card (GROWTH-6635).
      return apiClient({
        method: "post",
        responseType: "json",
        url: `/members/json-api${teamUrl}/payment/card-assign`,
        data: { ...data, ...userIdParams }
      })
        .then(() => {
          dispatch({
            type: payment.UPDATE_PAYMENT_METHOD
          });

          // Handed back about 1 ms later, not in this turn: React batches the two reducer runs of one turn, so the page would never
          // render with the flag on and its "subscription updated" message would not show. deleteCard does the same (GROWTH-6665).
          setTimeout(
            () =>
              dispatch({
                type: payment.UPDATE_PAYMENT_METHOD_RESET
              }),
            1
          );

          return dispatch(this.getPaymentMethods());
        })
        .catch(error => {
          // No payload: the reducer stores `true` under error.updatePaymentMethod (payment-019) and nothing reads that key.
          dispatch({
            type: payment.UPDATE_PAYMENT_METHOD_FAIL
          });
          // json-api answers { ok: false, error }; "" lets PaymentError show its own fallback when there is no message. An
          // object-shaped error is coerced to text first, as Notification does, so it cannot break the toast.
          dispatch(mainActions.togglePaymentError(true, getSafeErrorText(resolveApiErrorMessage(error, ""))));

          throw error;
        });
    };
  },

  getPdfFile(url) {
    return (dispatch, getState) => {
      if (!url) {
        return Promise.resolve();
      }

      const state = getState();
      const {
        teams: { currentTeam }
      } = state;
      const pendingLinks = pendingInvoiceLinks(state);

      // The same invoice twice (a double click, a held Enter) is one request. The row's button is disabled too; this
      // covers whatever reaches the thunk before the page has drawn that.
      if (pendingLinks.includes(url)) {
        return Promise.resolve();
      }

      dispatch(this.setLoading("invoicePdf", [...pendingLinks, url]));

      let request_url = url.startsWith("/members") ? url : `/members${url}`;

      if (currentTeam?.value) {
        request_url = request_url.replace("/members/json-api/", "/members/json-api/team/");
        const parsed = queryString.parseUrl(request_url);
        request_url = queryString.stringifyUrl({
          url: parsed.url,
          query: { ...parsed.query, user_id: currentTeam.value }
        });
      }

      // Returned so a caller can await the outcome; it never rejects, the failure is raised as a toast.
      return apiClient
        .get(request_url, { responseType: "blob" })
        .then(result => savePdf(result.data, resolvePdfFileName(result.headers)))
        .catch(async error => {
          dispatch(mainActions.togglePaymentError(true, await resolvePdfErrorMessage(error)));
        })
        .finally(() => {
          // Read again: other invoices may have started or ended while this one was on its way.
          dispatch(
            this.setLoading(
              "invoicePdf",
              pendingInvoiceLinks(getState()).filter(link => link !== url)
            )
          );
        });
    };
  },

  getPaymentHistory(page, perPage) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";

      paymentHistoryAbortController?.abort();
      const controller = new AbortController();
      paymentHistoryAbortController = controller;

      dispatch(this.setLoading("paymentHistory", true));
      const perPageParam = perPage ? { per_page: perPage } : {};

      const filterParams = queryString.stringify({ page, user_id: currentTeam.value, ...perPageParam }, { arrayFormat: "index" });

      apiClient({
        method: "get",
        responseType: "json",
        url: `/members/json-api${teamUrl}/payment/history?${filterParams}`,
        signal: controller.signal
      })
        .then(res => {
          // Aborted while the answer was on its way (the section closed): nobody asked for it any more (GROWTH-7382).
          if (controller.signal.aborted) {
            return;
          }

          const { payments, ...pagination } = res.data;
          dispatch({
            type: payment.GET_PAYMENT_HISTORY,
            payload: {
              payments,
              pagination
            }
          });
        })
        .catch(error => {
          // A request this thunk replaced: its replacement is still loading and answers for the flag, so a cancel
          // dispatches nothing (not the FAIL, and not a loading-off that would blink the page overlay).
          if (isCanceledRequest(error)) {
            return;
          }

          dispatch({
            type: payment.GET_PAYMENT_HISTORY_FAIL
          });
        })
        .finally(() => {
          // The cancelled request settles after its replacement has stored its own controller, so only the
          // controller that is still the current one may clear the holder.
          if (paymentHistoryAbortController === controller) {
            paymentHistoryAbortController = null;
          }
        });
    };
  },

  // The section closed (or the page was left): nobody is waiting for the request in flight, and the endpoint loads the
  // whole history before slicing. Aborts it and turns the flag off, because a cancel dispatches nothing and no replacement
  // is coming to answer for it. Does nothing when no request is in flight (GROWTH-7382).
  abortPaymentHistory() {
    return dispatch => {
      const controller = paymentHistoryAbortController;

      if (!controller) {
        return;
      }

      // Released before the abort, so the cancelled request's finally has nothing left to clear.
      paymentHistoryAbortController = null;
      controller.abort();
      dispatch(this.setLoading("paymentHistory", false));
    };
  },

  resetPaymentHistory() {
    return {
      type: payment.RESET_PAYMENT_HISTORY
    };
  },

  setLoading(section, isSectionLoading) {
    return {
      type: payment.SET_LOADING,
      payload: { section, isSectionLoading }
    };
  },

  selectCard(card) {
    return {
      type: payment.SET_SELECTED_CARD,
      payload: card
    };
  },

  updateCard(card) {
    return (dispatch, getState) => {
      const {
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};

      dispatch(this.setLoading("updateCard", true));
      dispatch(mainActions.togglePaymentError(false, ""));
      apiClient({
        method: "post",
        url: `/members/json-api${teamUrl}/payment/card-update`,
        headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        data: qs.stringify({ ...card, ...userIdParams })
      })
        .then(() => {
          new Promise(resolve => {
            dispatch({
              type: payment.UPDATE_CARD_SUCCESS,
              payload: true
            });

            if (card.default_source) {
              dispatch({
                type: payment.SET_DEFAULT_SOURCE,
                payload: card
              });
            }

            dispatch({
              type: payment.UPDATE_CARD_LOCALLY,
              payload: card
            });

            dispatch(this.selectCard(card));
            resolve();
          }).then(() => {
            dispatch({
              type: payment.UPDATE_CARD_SUCCESS,
              payload: null
            });
            dispatch(this.getPaymentMethods());
          });
        })
        .catch(error => {
          dispatch(mainActions.togglePaymentError(true, error?.response?.data?.error));
        })
        .finally(() => dispatch(this.setLoading("updateCard", false)));
    };
  },

  updateDefaultSource(cardId) {
    return (dispatch, getState) => {
      const {
        payment: { paymentMethods, defaultSource: previousDefaultSource },
        teams: { currentTeam }
      } = getState();
      const teamUrl = currentTeam?.value ? "/team" : "";
      const userIdParams = currentTeam?.value ? { user_id: currentTeam.value } : {};
      const selectedCard = paymentMethods?.find(({ id }) => id === cardId);

      if (selectedCard) {
        dispatch({
          type: payment.SET_DEFAULT_SOURCE,
          payload: { ...selectedCard, default_source: true }
        });
      }

      // SET_DEFAULT_SOURCE leaves the loading flags alone (GROWTH-6634), so this flag no longer has to wait for the optimistic
      // update; the order is kept as paymentActions-033 pins it.
      dispatch(this.setLoading("updateCard", true));
      dispatch(mainActions.togglePaymentError(false, ""));

      return apiClient({
        method: "post",
        url: `/members/json-api${teamUrl}/payment/card-default`,
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        data: {
          card_id: cardId,
          ...userIdParams
        }
      })
        .then(res => {
          const stripeDefaultId = res.data?.default_source || cardId;
          const updatedDefault = paymentMethods?.find(({ id }) => id === stripeDefaultId) || {
            ...selectedCard,
            id: stripeDefaultId,
            default_source: true
          };

          dispatch({
            type: payment.SET_DEFAULT_SOURCE,
            payload: updatedDefault
          });

          dispatch({
            type: payment.UPDATE_CARD_SUCCESS,
            payload: true
          });

          // Same reset as deleteCard: the page's notification effect needs a render with the flag set before it is cleared.
          setTimeout(
            () =>
              dispatch({
                type: payment.UPDATE_CARD_SUCCESS,
                payload: null
              }),
            1
          );
        })
        .catch(error => {
          dispatch({
            type: payment.SET_DEFAULT_SOURCE,
            payload: previousDefaultSource || null
          });

          // With no previous default the reducer cannot clear the flag on the card the optimistic update marked.
          if (!previousDefaultSource && selectedCard) {
            dispatch({ type: payment.UPDATE_CARD_LOCALLY, payload: selectedCard });
          }

          dispatch(mainActions.togglePaymentError(true, error?.response?.data?.error));

          // Rejects, so a caller can revert its own state; callers with nothing to revert swallow it.
          throw error;
        })
        .finally(() => dispatch(this.setLoading("updateCard", false)));
    };
  },

  setErrorToken(err) {
    return dispatch => {
      dispatch({ type: payment.GET_ERROR_TOKEN, payload: err.message });
    };
  },

  clearTokenError() {
    return dispatch => {
      dispatch({ type: payment.CLEAR_ERROR_TOKEN });
      // Every Add Card submit calls this. Clearing the server's last rejection too means a failure on this attempt
      // mounts a fresh notification, not one whose 7-second timer has already hidden it.
      dispatch({
        type: payment.ADD_CARD_FAIL,
        payload: null
      });
    };
  }
};
